import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import api from './api';
import { useAuth } from './Auth';
import { usePlanAccess } from './PlanAccess';
import { StoreProfile } from '../models';

const SELECTED_STORE_STORAGE_KEY = 'vioo_selected_store_id';

interface StoreSelectionContextValue {
  loading: boolean;
  stores: StoreProfile[];
  selectedStore: StoreProfile | null;
  selectedStoreId: number | null;
  setSelectedStoreId: (id: number | null) => void;
  refresh: () => Promise<void>;
}

const StoreSelectionContext = createContext<
  StoreSelectionContextValue | undefined
>(undefined);

interface StoreSelectionProviderProps {
  children: React.ReactNode;
}

function readStoredStoreId(): number | null {
  const raw = localStorage.getItem(SELECTED_STORE_STORAGE_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export const StoreSelectionProvider: React.FC<StoreSelectionProviderProps> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const { canAccessStores, loading: planLoading } = usePlanAccess();
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState<StoreProfile[]>([]);
  const [selectedStoreId, setSelectedStoreIdState] = useState<number | null>(
    readStoredStoreId
  );

  const setSelectedStoreId = useCallback((id: number | null) => {
    setSelectedStoreIdState(id);
    if (id === null) {
      localStorage.removeItem(SELECTED_STORE_STORAGE_KEY);
    } else {
      localStorage.setItem(SELECTED_STORE_STORAGE_KEY, String(id));
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !canAccessStores) {
      setStores([]);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get<StoreProfile[]>('v1/store/');
      const nextStores = response.data ?? [];
      setStores(nextStores);

      setSelectedStoreIdState((current) => {
        if (current && nextStores.some((store) => store.id === current)) {
          return current;
        }
        const firstId = nextStores[0]?.id ?? null;
        if (firstId === null) {
          localStorage.removeItem(SELECTED_STORE_STORAGE_KEY);
        } else {
          localStorage.setItem(SELECTED_STORE_STORAGE_KEY, String(firstId));
        }
        return firstId;
      });
    } catch (error) {
      console.error('Failed to load stores for header selection', error);
      setStores([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, canAccessStores]);

  useEffect(() => {
    if (planLoading) return;
    if (!isAuthenticated || !canAccessStores) {
      setStores([]);
      return;
    }
    refresh();
  }, [planLoading, isAuthenticated, canAccessStores, refresh]);

  const selectedStore = useMemo(
    () => stores.find((store) => store.id === selectedStoreId) ?? null,
    [stores, selectedStoreId]
  );

  const value = useMemo<StoreSelectionContextValue>(
    () => ({
      loading,
      stores,
      selectedStore,
      selectedStoreId,
      setSelectedStoreId,
      refresh,
    }),
    [
      loading,
      stores,
      selectedStore,
      selectedStoreId,
      setSelectedStoreId,
      refresh,
    ]
  );

  return (
    <StoreSelectionContext.Provider value={value}>
      {children}
    </StoreSelectionContext.Provider>
  );
};

export function useStoreSelection(): StoreSelectionContextValue {
  const context = useContext(StoreSelectionContext);
  if (context === undefined) {
    throw new Error(
      'useStoreSelection must be used within a StoreSelectionProvider'
    );
  }
  return context;
}
